import {
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { useEffect } from "react";
import { DetailsApiFactory } from "../lusca-search-api/LuscaSearchApiFactory";
import { useHealthStateUpdate } from "../contexes/HealthStateContext";
import { AccountingEventsGroup } from "../types/events";
import { DigitalBusinessDocument } from "../types/documents";
import { CanonicalBusinessEvent } from "../types/cbes";
import useFetchDlqInfo from "./useFetchDlqInfo";
import { calculateCompleteHealthState } from "../helpers/healthUtils";
import { isFreeTrial as checkFreeTrialStatus } from "../helpers/freeTrialUtils";
import { DetailsResponse, Stage } from "../lusca-search-api/generated-src";
import removeEmpty from "../helpers/removeEmpty";
import { useGlProduct } from "../contexes/GlProductContext";
import { useDeferralId } from "../contexes/DeferralIdContext";
import { getDeferralIds } from "../helpers/cbeSummaryUtils";
import { decompressAndDeserializeData } from "../helpers/compressionUtils";
import { isAccountingEventsGroup } from "../helpers/typeChecks";
import {
  cacheFallbackDetails,
  FallbackResponse,
  getDetailsQueryKey,
} from "../helpers/detailsCacheUtils";

export interface DetailsDataInterface {
  events: (AccountingEventsGroup | DigitalBusinessDocument)[];
  cbes: CanonicalBusinessEvent[];
  fallback?: FallbackResponse;
  auids?: string[];
  cachedFromFallback?: boolean;
}

const processRawData = (data: DetailsResponse): DetailsDataInterface => {
  const events = (data.events || []).map((event) => {
    const cleanEvent = removeEmpty(event);

    if (cleanEvent.body && typeof cleanEvent.body === "string") {
      cleanEvent.body = decompressAndDeserializeData(cleanEvent.body);
    }

    if ("accountingEvents" in cleanEvent && typeof cleanEvent.sk === "string") {
      cleanEvent.sk = parseInt(cleanEvent.sk, 10);
    }
    if (
      "documentType" in cleanEvent &&
      typeof cleanEvent.version === "string"
    ) {
      cleanEvent.version = parseInt(cleanEvent.version, 10);
    }

    return "documentType" in cleanEvent
      ? (cleanEvent as DigitalBusinessDocument)
      : (cleanEvent as AccountingEventsGroup);
  });

  const cbes = (data.cbes || []).map((cbe) => {
    const cleanCbe = removeEmpty(cbe);

    if (
      cleanCbe.businessEvents &&
      typeof cleanCbe.businessEvents === "string"
    ) {
      cleanCbe.businessEvents = decompressAndDeserializeData(
        cleanCbe.businessEvents
      );
    }

    if (cleanCbe.lockVersion && typeof cleanCbe.lockVersion === "string") {
      cleanCbe.lockVersion = parseInt(cleanCbe.lockVersion, 10);
    }

    return cleanCbe as CanonicalBusinessEvent;
  });

  return {
    events,
    cbes,
    fallback: data.fallback,
    auids: data.auids,
  };
};

export default function useFetchDetails(
  stage: Stage,
  orderId: string
): UseQueryResult<DetailsDataInterface, Error> {
  const DetailsApi = DetailsApiFactory();
  const { setState: setHealthState } = useHealthStateUpdate();
  const dlqInfo = useFetchDlqInfo(stage, orderId);
  const { glProduct } = useGlProduct();
  const { setDeferralIds } = useDeferralId();
  const queryClient = useQueryClient();

  const query = useQuery<DetailsDataInterface, Error>(
    getDetailsQueryKey(stage, orderId),
    async () => {
      const { data } = await DetailsApi.getDetails(stage, orderId);
      const processed = processRawData(data);

      // Process deferral IDs from all CBEs
      if (processed.cbes && processed.cbes.length > 0) {
        let revenueDeferralId = "";
        let royaltyDeferralId = "";

        processed.cbes.forEach((cbe) => {
          const deferralIds = getDeferralIds(cbe);
          if (deferralIds.revenueDeferralId) {
            revenueDeferralId = deferralIds.revenueDeferralId;
          }
          if (deferralIds.royaltyDeferralId) {
            royaltyDeferralId = deferralIds.royaltyDeferralId;
          }
        });

        // Update the context with all found deferral IDs
        setDeferralIds({
          revenueDeferralId,
          royaltyDeferralId,
        });
      }

      const freeTrialStatus = checkFreeTrialStatus(processed.events);
      const newHealthState = calculateCompleteHealthState(
        processed.events,
        processed.cbes,
        dlqInfo,
        glProduct,
        freeTrialStatus
      );
      setHealthState(newHealthState);
      cacheFallbackDetails(queryClient, stage, processed);

      return processed;
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: false,
    }
  );

  useEffect(() => {
    setHealthState((prevState) => ({
      ...prevState,
      overallHealth: "unknown",
    }));
  }, [setHealthState]);

  // Update health state when DLQ info changes
  useEffect(() => {
    if (query.data) {
      const freeTrialStatus = checkFreeTrialStatus(query.data.events);
      const newHealthState = calculateCompleteHealthState(
        query.data.events,
        query.data.cbes,
        dlqInfo,
        glProduct,
        freeTrialStatus
      );
      setHealthState(newHealthState);
    }
  }, [
    dlqInfo.status,
    dlqInfo.dlqItems?.length,
    query.data,
    glProduct,
    setHealthState,
  ]);

  return query;
}
